import { readableForeground } from "@/theme/contrast";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Alert,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  List as ListIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/context/AppContext";
import { useCalendarData } from '@/shows/hooks/useCalendarData';
import { apiDateToDateOnly, localDateToDateOnly } from "@/shows/utils/dateOnly";
import { getPositionText } from '@/shows/utils/formatters';
import type { ShowList, HoldList } from "api-client/models";
import { HoldQuickCreateModal } from "@/bookings/components/HoldQuickCreateModal";
import AdminJson from "@/utilities/AdminJson";

import { CalendarAgenda } from './CalendarAgenda';

export const ShowCalendar: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isDevMode, team } = useAppContext();
  const [currentDate, setCurrentDate] = useState(new Date());
  const { shows, holds, loading, error, refresh } = useCalendarData(currentDate);
  const [showReleasedHolds, setShowReleasedHolds] = useState(false);

  // Multi-day hold drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Date | null>(null);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Hover state for multi-day hold highlighting
  const [hoveredShowId, setHoveredShowId] = useState<string | null>(null);

  useEffect(() => {
    setIsDragging(false);
    setDragStart(null);
    setSelectedDates([]);
    setShowCreateModal(false);
    setHoveredShowId(null);
  }, [team?.id]);

  const getShowsForDate = (date: Date): ShowList[] => {
    const dateStr = localDateToDateOnly(date);
    return shows.filter((show) => {
      const showDateStr = apiDateToDateOnly(show.showDate);
      return showDateStr === dateStr;
    });
  };

  const getHoldsForDate = (date: Date): HoldList[] => {
    const dateStr = localDateToDateOnly(date);
    return holds.filter((hold) => {
      const holdDateStr = apiDateToDateOnly(hold.holdDate);

      // Match the date
      if (holdDateStr !== dateStr) return false;

      // Filter out released holds unless toggle is on (client-side filtering)
      if (hold.status === "released" && !showReleasedHolds) {
        return false;
      }

      return true;
    });
  };

  // Date-specific confirmation is displayed independently of hold creation availability.
  // ARCHITECTURE NOTE: Holds are the source of truth for date-specific status
  // - Show entity = master/parent (the playbill, residency, tour stop)
  // - Hold entity = date-specific child with its own status per date
  // A multi-day show can have different statuses on different dates (some confirmed, some pending)
  const hasConfirmedShow = (date: Date): boolean => {
    const dateStr = localDateToDateOnly(date);

    // Primary check: Holds are the source of truth for date-specific confirmation status
    // When hold.status === "confirmed", that date is confirmed for that show
    return holds.some((hold) => {
      const holdDateStr = apiDateToDateOnly(hold.holdDate);
      return holdDateStr === dateStr && hold.status === "confirmed";
    });
  };

  // Helper function to get all holds for a specific show (multi-day grouping)
  const getHoldsByShow = (showId: string | undefined): HoldList[] => {
    if (!showId) return [];
    return holds.filter((hold) => hold.show === showId);
  };

  // Helper function to check if hold spans multiple days
  const isMultiDayHold = (hold: HoldList): boolean => {
    if (!hold.show) return false;
    const showHolds = getHoldsByShow(hold.show);
    return showHolds.length > 1;
  };

  // Generate consistent color based on show title (hash string to color)
  const getShowColor = (showTitle: string) => {
    if (!showTitle) return "#90caf9"; // Default light blue for untitled shows

    // Generate hash from string
    let hash = 0;
    for (let i = 0; i < showTitle.length; i++) {
      hash = showTitle.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Predefined color palette (vibrant, accessible colors)
    const colors = [
      "#1976d2", // Blue
      "#d32f2f", // Red
      "#388e3c", // Green
      "#f57c00", // Orange
      "#7b1fa2", // Purple
      "#0288d1", // Light Blue
      "#c2185b", // Pink
      "#0097a7", // Cyan
      "#689f38", // Light Green
      "#fbc02d", // Yellow
      "#5d4037", // Brown
      "#455a64", // Blue Grey
      "#e64a19", // Deep Orange
      "#303f9f", // Indigo
      "#00796b", // Teal
    ];

    // Use hash to pick color from palette
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (): Date[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Date[] = [];

    // Add empty days for the start of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthDay = new Date(year, month, -startingDayOfWeek + i + 1);
      days.push(prevMonthDay);
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    // Add days to complete the last week + one extra week from next month
    const remainingDays = 7 - (days.length % 7);
    const totalDaysToAdd = remainingDays < 7 ? remainingDays + 7 : 7; // Always add at least one full week

    for (let i = 1; i <= totalDaysToAdd; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  };

  const previousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1),
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1),
    );
  };

  const today = () => {
    setCurrentDate(new Date());
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth();
  };

  const isToday = (date: Date): boolean => {
    const now = new Date();
    return (
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    );
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Drag selection handlers for multi-day holds
  const handleMouseDown = (date: Date) => {
    // Don't allow selecting past dates
    if (isPastDate(date)) return;

    setIsDragging(true);
    setDragStart(date);
    setSelectedDates([date]);
  };

  const handleMouseEnter = (date: Date) => {
    if (!isDragging || !dragStart) return;

    // Only update if not a past date
    if (isPastDate(date)) return;

    // Calculate all dates between dragStart and current date
    const start = dragStart.getTime();
    const end = date.getTime();
    const min = Math.min(start, end);
    const max = Math.max(start, end);

    const dates: Date[] = [];
    const current = new Date(min);
    while (current.getTime() <= max) {
      // Only include dates that are not in the past
      if (!isPastDate(current)) {
        // Create new Date with explicit year/month/day to avoid timezone issues
        dates.push(
          new Date(
            current.getFullYear(),
            current.getMonth(),
            current.getDate(),
          ),
        );
      }
      current.setDate(current.getDate() + 1);
    }

    setSelectedDates(dates);
  };

  const handleMouseUp = () => {
    if (!isDragging || selectedDates.length === 0) {
      setIsDragging(false);
      return;
    }

    // Show the quick create modal (keep selectedDates to maintain highlighting)
    setShowCreateModal(true);
    setIsDragging(false);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    // Clear selection after a small delay to allow smooth transition
    setTimeout(() => {
      setDragStart(null);
      setSelectedDates([]);
    }, 100);
  };

  const handleHoldCreated = () => {
    // Preserve the open dialog and selected dates while displaying partial results.
    refresh();
  };

  const isDateSelected = (date: Date): boolean => {
    return selectedDates.some(
      (selectedDate) =>
        selectedDate.getDate() === date.getDate() &&
        selectedDate.getMonth() === date.getMonth() &&
        selectedDate.getFullYear() === date.getFullYear(),
    );
  };

  const days = getDaysInMonth();

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Show Calendar
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your show schedule in calendar format
          </Typography>
          <Typography
            variant="caption"
            color="readable.primary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {isMobile ? 'Tap Add hold on a day, or New Hold for multiple dates.' : '💡 Tip: Click and drag across dates to create multi-day holds'}
          </Typography>
        </Box>
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ListIcon />}
            onClick={() => navigate("/shows")}
          >
            List View
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate("/bookings/new")}
            color="secondary"
          >
            New Hold
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/shows/new")}
          >
            New Show
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Calendar Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ flexWrap: "wrap", gap: 1 }}
            mb={3}
          >
            <Button
              startIcon={<ChevronLeftIcon />}
              onClick={previousMonth}
              variant="outlined"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              Previous
            </Button>
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                flexWrap: "wrap",
                gap: 1,
                minWidth: 0,
                maxWidth: "100%",
                order: { xs: -1, md: 0 },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Typography variant="h5">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </Typography>
              <Button onClick={today} variant="outlined" size="small">
                Today
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    checked={showReleasedHolds}
                    onChange={(e) => setShowReleasedHolds(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Show Released
                  </Typography>
                }
              />
            </Stack>
            <Button
              endIcon={<ChevronRightIcon />}
              onClick={nextMonth}
              variant="outlined"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              Next
            </Button>
          </Stack>

          {loading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading calendar...</Typography>
            </Stack>
          ) : isMobile ? (
            <CalendarAgenda days={days.filter(isCurrentMonth)} getHolds={getHoldsForDate} getShows={getShowsForDate}
              canCreate={(date) => !isPastDate(date)}
              onCreate={(date) => { setSelectedDates([date]); setShowCreateModal(true); }}
              onShow={(showId) => navigate(`/shows/${showId}`)} />
          ) : (
            <Box
              role="region"
              aria-label="Monthly calendar"
              tabIndex={0}
              sx={{ overflowX: "auto" }}
            >
              {/* Day of week headers */}
              <Box
                display="grid"
                gridTemplateColumns="repeat(7, 1fr)"
                mb={0}
                sx={{ minWidth: 840 }}
              >
                {DAYS_OF_WEEK.map((day) => (
                  <Box
                    key={day}
                    textAlign="center"
                    py={1}
                    sx={{
                      borderTop: 1,
                      borderBottom: 1,
                      borderRight: 1,
                      borderColor: "divider",
                      "&:first-of-type": {
                        borderLeft: 1,
                      },
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {day}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Calendar grid */}
              <Box
                display="grid"
                gridTemplateColumns="repeat(7, 1fr)"
                sx={{ minWidth: 840 }}
                onMouseUp={handleMouseUp}
                onMouseLeave={() => {
                  // Only cancel drag if we're actively dragging (don't clear if modal is about to open)
                  if (isDragging) {
                    setIsDragging(false);
                    setDragStart(null);
                    // Don't clear selectedDates here - let handleModalClose handle it
                  }
                }}
              >
                {days.map((date, index) => {
                  const dayShows = getShowsForDate(date);
                  const dayHolds = getHoldsForDate(date);
                  const isCurrent = isCurrentMonth(date);
                  const isNow = isToday(date);
                  const isSelected = isDateSelected(date);
                  const isConfirmed = hasConfirmedShow(date);

                  return (
                    <Card
                      key={index}
                      variant="outlined"
                      onMouseDown={(event) => {
                        const target = event.target as HTMLElement;
                        // If interaction started on a chip, let chip handlers own the event.
                        if (target.closest(".MuiChip-root")) {
                          return;
                        }
                        handleMouseDown(date);
                      }}
                      onMouseEnter={() => handleMouseEnter(date)}
                      sx={{
                        minHeight: 120,
                        position: "relative",
                        bgcolor: isSelected
                          ? "surfaces.inset"
                          : isConfirmed
                            ? "rgba(255, 152, 0, 0.12)" // Light orange for confirmed/advancement phase
                            : isNow
                              ? "primary.light"
                              : isCurrent
                                ? "background.paper"
                                : "action.hover",
                        color: "text.secondary",
                        cursor: isPastDate(date) ? "not-allowed" : "crosshair", // Allow crosshair for all future dates (current and next month)
                        // Compact border style - colored borders on left and top
                        border: "none",
                        // Right border (always thin divider)
                        borderRight: "1px solid",
                        borderRightColor: "divider",
                        // Bottom border (always thin divider)
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                        // Left border (colored for selected/confirmed, first column only)
                        borderLeft: isSelected
                          ? "3px solid"
                          : isConfirmed
                            ? "3px solid"
                            : index % 7 === 0
                              ? "1px solid"
                              : "none",
                        borderLeftColor: isSelected
                          ? "readable.link"
                          : isConfirmed
                            ? "statusText.attention" // Confirmed-date boundary
                            : "divider",
                        // Top border (colored for selected/confirmed, first row only)
                        borderTop: isSelected
                          ? "3px solid"
                          : isConfirmed
                            ? "3px solid"
                            : index < 7
                              ? "1px solid"
                              : "none",
                        borderTopColor: isSelected
                          ? "readable.link"
                          : isConfirmed
                            ? "statusText.attention" // Confirmed-date boundary
                            : "divider",
                        borderRadius: 0, // Remove border radius for seamless grid
                        userSelect: "none",
                        transition: "box-shadow 0.2s ease",
                        "&:hover":
                          !isConfirmed && !isPastDate(date)
                            ? {
                                boxShadow: 2,
                                bgcolor: isSelected
                                  ? "surfaces.inset"
                                  : "action.hover",
                                zIndex: 1,
                              }
                            : {},
                      }}
                    >
                      <CardContent sx={{ p: 1 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={
                                isNow || isSelected ? "bold" : "normal"
                              }
                              color="text.primary"
                            >
                              {date.getDate()}
                            </Typography>
                            {/* Confirmed label inline with date */}
                            {isConfirmed && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.6rem",
                                  fontWeight: "bold",
                                  color: "statusText.attention",
                                  textTransform: "uppercase",
                                }}
                              >
                                ✓ Confirmed
                              </Typography>
                            )}
                          </Stack>
                        </Stack>

                        <Stack spacing={0.5}>
                          {/* Show confirmed shows first */}
                          {dayShows.map((show) => (
                            <Box key={show.id}>
                              <Chip
                                label={show.title}
                                size="small"
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(`/shows/${show.id}`);
                                }}
                                sx={{
                                  height: "auto",
                                  backgroundColor: "#FF4500",
                                  color: readableForeground("#FF4500"),
                                  fontWeight: "bold",
                                  "& .MuiChip-label": {
                                    display: "block",
                                    whiteSpace: "normal",
                                    fontSize: "0.7rem",
                                    py: 0.5,
                                  },
                                }}
                              />
                            </Box>
                          ))}

                          {/* Show confirmed holds (display like confirmed shows) */}
                          {dayHolds
                            .filter((hold) => hold.status === "confirmed")
                            .map((hold) => (
                              <Box key={hold.id}>
                                <Chip
                                  label={hold.showTitle || "Confirmed Show"}
                                  size="small"
                                  onMouseDown={(event) => {
                                    event.stopPropagation();
                                  }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (hold.show) {
                                      navigate(`/shows/${hold.show}`);
                                    } else {
                                      navigate(`/bookings/${hold.id}`);
                                    }
                                  }}
                                  sx={{
                                    height: "auto",
                                    backgroundColor: "#FF4500",
                                    color: readableForeground("#FF4500"),
                                    fontWeight: "bold",
                                    "& .MuiChip-label": {
                                      display: "block",
                                      whiteSpace: "normal",
                                      fontSize: "0.7rem",
                                      py: 0.5,
                                    },
                                  }}
                                />
                              </Box>
                            ))}

                          {/* Show pending/challenged/released holds with position badges */}
                          {dayHolds
                            .filter((hold) => {
                              // Filter out expired and confirmed holds (confirmed shown separately above)
                              return (
                                hold.status !== "expired" &&
                                hold.status !== "confirmed"
                              );
                            })
                            .sort(
                              (a, b) =>
                                (a.holdPosition || 0) - (b.holdPosition || 0),
                            )
                            .map((hold) => {
                              const isChallenged = hold.status === "challenged";
                              const isReleased = hold.status === "released";
                              const showColor = getShowColor(
                                hold.showTitle || "",
                              );
                              const displayName =
                                hold.showTitle || "Untitled Hold";
                              const positionLabel = getPositionText(
                                hold.holdPosition || 0,
                              );
                              const multiDay = isMultiDayHold(hold);
                              const isHighlighted =
                                hoveredShowId && hold.show === hoveredShowId;

                              return (
                                <Chip
                                  key={hold.id}
                                  label={
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                    >
                                      <span>
                                        {positionLabel}: {displayName}
                                        {isReleased && " (Released)"}
                                      </span>
                                    </Stack>
                                  }
                                  size="small"
                                  onMouseEnter={() => {
                                    if (hold.show && multiDay) {
                                      setHoveredShowId(hold.show);
                                    }
                                  }}
                                  onMouseLeave={() => setHoveredShowId(null)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (hold.show) {
                                      navigate(`/shows/${hold.show}`);
                                    } else {
                                      navigate(`/bookings/${hold.id}`);
                                    }
                                  }}
                                  sx={{
                                    height: "auto",
                                    backgroundColor: showColor,
                                    color: readableForeground(showColor),
                                    textDecoration: isReleased
                                      ? "line-through"
                                      : "none",
                                    border: isChallenged
                                      ? "2px solid #ff9800"
                                      : multiDay
                                        ? `2px dotted ${showColor}`
                                        : "2px solid transparent",
                                    cursor: "pointer",
                                    transition:
                                      "box-shadow 0.2s ease",
                                    outline: isHighlighted
                                      ? "3px solid"
                                      : "none",
                                    outlineColor: "readable.link",
                                    outlineOffset: 2,
                                    boxShadow: isHighlighted ? 2 : 0,
                                    "&:hover": {
                                      boxShadow: 2,
                                      outline: "2px solid",
                                      outlineColor: "readable.link",
                                    },
                                    "& .MuiChip-label": {
                                      display: "block",
                                      whiteSpace: "normal",
                                      fontSize: "0.65rem",
                                      py: 0.5,
                                      fontWeight: "bold",
                                    },
                                  }}
                                />
                              );
                            })}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Quick Create Hold Modal */}
      <HoldQuickCreateModal
        open={showCreateModal}
        onClose={handleModalClose}
        selectedDates={selectedDates}
        onSuccess={handleHoldCreated}
      />
      <AdminJson
        isDebug={isDevMode}
        data={{
          shows,
          holds,
          selectedDates,
          hoveredShowId,
          dragStart,
          isDragging,
        }}
      />
    </Box>
  );
};
